import { sfx } from "../sfx/SfxEngine";
import type { ReportCardModel } from "@piano-tutor/shared";
import {
  shareReportCard,
  downloadReportCard,
  type ShareReportInput,
} from "../shareReportCard";

export type ResultsPayload = {
  kind: "win" | "lose" | "class" | "arcade" | "report";
  title: string;
  subtitle?: string;
  score: number;
  lines?: Array<{ label: string; value: string; tip?: string }>;
  report?: ReportCardModel;
  /** Show Restart Class (class report only). */
  showRestart?: boolean;
  /** Pride-share card for social media. */
  share?: ShareReportInput;
};

export type ResultsCloseAction = "continue" | "restart" | "dismiss";

/**
 * WWE / sports-title style score reveal + classic report card (NPM / jitter / heatmap).
 */
export class ResultsOverlay {
  readonly root: HTMLElement;
  private scoreEl: HTMLElement;
  private titleEl: HTMLElement;
  private subEl: HTMLElement;
  private linesEl: HTMLElement;
  private reportEl: HTMLElement;
  private heatmap: HTMLCanvasElement;
  private restartBtn: HTMLButtonElement;
  private shareBtn: HTMLButtonElement;
  private raf = 0;
  private onClose: ((action: ResultsCloseAction) => void) | null = null;
  private lastReport: ReportCardModel | null = null;
  private lastShare: ShareReportInput | null = null;
  private closeAction: ResultsCloseAction = "continue";

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.id = "results-overlay";
    this.root.className = "results-overlay";
    this.root.hidden = true;
    this.root.setAttribute("aria-hidden", "true");
    this.root.innerHTML = `
      <div class="results-card">
        <div class="results-burst" aria-hidden="true"></div>
        <p class="results-eyebrow" id="results-eyebrow">RESULT</p>
        <h2 class="results-title" id="results-title">CLEAR</h2>
        <p class="results-sub" id="results-sub"></p>
        <div class="results-score-wrap">
          <span class="results-score-label">SCORE</span>
          <span class="results-score" id="results-score">0</span>
        </div>
        <ul class="results-lines" id="results-lines"></ul>
        <div class="report-card-block" id="report-card-block" hidden>
          <div class="report-grade-row">
            <span class="report-letter" id="report-letter">A</span>
            <div>
              <div class="report-npm"><strong id="report-npm">0</strong> NPM</div>
              <div class="report-meta" id="report-meta"></div>
            </div>
          </div>
          <canvas id="report-heatmap" width="320" height="56" aria-label="Error heatmap"></canvas>
          <p class="report-heatmap-caption">Error heatmap (darker = more misses on that pitch)</p>
          <dl class="report-glossary" id="report-glossary">
            <div><dt>Score</dt><dd>Accuracy×50 + Perfect×8 + Hits×2 − Wrongs×6 + steady-timing bonus (up to 400).</dd></div>
            <div><dt>Jitter</dt><dd>Timing consistency — how much your hit timing spreads around your average (lower is steadier).</dd></div>
            <div><dt>Bias</dt><dd>Average early/late error — negative = rushing, positive = dragging behind the beat.</dd></div>
            <div><dt>NPM</dt><dd>Notes cleared per minute (typing-tutor pace).</dd></div>
            <div><dt>Accuracy</dt><dd>Correct hits ÷ (hits + wrong presses) for the whole class.</dd></div>
          </dl>
        </div>
        <p class="results-share-nudge" id="results-share-nudge" hidden>
          Proud of this run? Share your report card — inspire a friend to start piano.
        </p>
        <div class="results-actions">
          <button type="button" class="secondary" id="results-pdf" hidden>Save report</button>
          <button type="button" class="secondary" id="results-download" hidden>Save image</button>
          <button type="button" class="secondary" id="results-share" hidden>Share</button>
          <button type="button" class="secondary" id="results-restart" hidden>Restart</button>
          <button type="button" class="btn-gold" id="results-continue">CONTINUE</button>
        </div>
      </div>
    `;
    parent.appendChild(this.root);
    this.titleEl = this.root.querySelector("#results-title")!;
    this.subEl = this.root.querySelector("#results-sub")!;
    this.scoreEl = this.root.querySelector("#results-score")!;
    this.linesEl = this.root.querySelector("#results-lines")!;
    this.reportEl = this.root.querySelector("#report-card-block")!;
    this.heatmap = this.root.querySelector("#report-heatmap")!;
    this.restartBtn = this.root.querySelector("#results-restart")!;
    this.shareBtn = this.root.querySelector("#results-share")!;
    const downloadBtn = this.root.querySelector<HTMLButtonElement>("#results-download")!;
    this.root.querySelector("#results-continue")!.addEventListener("click", () => {
      this.closeAction = "continue";
      this.hide();
    });
    this.restartBtn.addEventListener("click", () => {
      this.closeAction = "restart";
      this.hide();
    });
    this.shareBtn.addEventListener("click", () => {
      void this.doShare();
    });
    downloadBtn.addEventListener("click", () => {
      void this.doDownload();
    });
    this.root.querySelector("#results-pdf")!.addEventListener("click", () => this.printReport());
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root) {
        this.closeAction = "dismiss";
        this.hide();
      }
    });
  }

  private async doDownload(): Promise<void> {
    if (!this.lastShare) return;
    const nudge = this.root.querySelector<HTMLElement>("#results-share-nudge")!;
    const btn = this.root.querySelector<HTMLButtonElement>("#results-download")!;
    try {
      btn.disabled = true;
      btn.textContent = "Saving…";
      await downloadReportCard(this.lastShare);
      nudge.hidden = false;
      nudge.textContent = "Report card image saved to your device!";
      sfx.play("confirm");
    } catch (e) {
      nudge.hidden = false;
      nudge.textContent = e instanceof Error ? e.message : "Download failed.";
    } finally {
      btn.disabled = false;
      btn.textContent = "Save image";
    }
  }

  private async doShare(): Promise<void> {
    if (!this.lastShare) return;
    const nudge = this.root.querySelector<HTMLElement>("#results-share-nudge")!;
    try {
      this.shareBtn.disabled = true;
      this.shareBtn.textContent = "Sharing…";
      const how = await shareReportCard(this.lastShare);
      nudge.hidden = false;
      nudge.textContent =
        how === "shared"
          ? "Shared! Your name + score + download link went out."
          : how === "downloaded"
            ? "Image saved + caption copied — post it on Instagram / WhatsApp."
            : "Caption copied — paste it with your report image.";
      sfx.play("confirm");
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      nudge.hidden = false;
      nudge.textContent = e instanceof Error ? e.message : "Share failed — try Save report.";
    } finally {
      this.shareBtn.disabled = false;
      this.shareBtn.textContent = "Share";
    }
  }

  show(payload: ResultsPayload, onClose?: (action: ResultsCloseAction) => void): void {
    cancelAnimationFrame(this.raf);
    this.onClose = onClose ?? null;
    this.closeAction = "continue";
    this.root.hidden = false;
    this.root.setAttribute("aria-hidden", "false");
    this.root.classList.remove("is-win", "is-lose", "is-on", "is-report");
    void this.root.offsetWidth;
    this.root.classList.add("is-on");
    this.root.classList.add(payload.kind === "lose" ? "is-lose" : "is-win");
    if (payload.kind === "report" || payload.kind === "class" || payload.report) {
      this.root.classList.add("is-report");
    }

    const eyebrow = this.root.querySelector("#results-eyebrow")!;
    eyebrow.textContent =
      payload.kind === "lose"
        ? "DEFEAT"
        : payload.kind === "arcade"
          ? "RUN OVER"
          : payload.kind === "report" || payload.kind === "class"
            ? "REPORT CARD"
            : "VICTORY";

    this.titleEl.textContent = payload.title;
    this.subEl.textContent = payload.subtitle ?? "";
    this.linesEl.innerHTML = (payload.lines ?? [])
      .map((l) => {
        const tip = l.tip ? ` title="${escapeAttr(l.tip)}"` : "";
        return `<li${tip}><span>${escape(l.label)}</span><strong>${escape(l.value)}</strong></li>`;
      })
      .join("");

    const pdfBtn = this.root.querySelector<HTMLButtonElement>("#results-pdf")!;
    const nudge = this.root.querySelector<HTMLElement>("#results-share-nudge")!;
    nudge.hidden = true;
    if (payload.report) {
      this.lastReport = payload.report;
      this.reportEl.hidden = false;
      pdfBtn.hidden = false;
      this.root.querySelector("#report-letter")!.textContent = payload.report.letter;
      this.root.querySelector("#report-npm")!.textContent = String(payload.report.npm);
      this.root.querySelector("#report-meta")!.textContent =
        `${payload.report.timingNote} · ${payload.report.dynamicsNote}`;
      this.drawHeatmap(payload.report.errorByMidi);
    } else {
      this.lastReport = null;
      this.reportEl.hidden = true;
      pdfBtn.hidden = true;
    }

    this.lastShare = payload.share ?? null;
    this.shareBtn.hidden = !payload.share;
    const downloadBtn = this.root.querySelector<HTMLButtonElement>("#results-download")!;
    downloadBtn.hidden = !payload.share;
    if (payload.share) {
      nudge.hidden = false;
      nudge.textContent =
        "Proud of this run? Share or save your report card — make friends jealous.";
    }

    this.restartBtn.hidden = !payload.showRestart;

    if (payload.kind === "lose") sfx.play("lose");
    else sfx.play("win");

    const target = Math.max(0, Math.round(payload.score));
    const start = performance.now();
    const dur = Math.min(1600, 500 + Math.min(target, 800) * 0.9);
    let lastTick = 0;

    const step = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(target * eased);
      this.scoreEl.textContent = String(val);
      if (now - lastTick > 55 && p < 1) {
        lastTick = now;
        sfx.play("scoreTick");
      }
      if (p < 1) {
        this.raf = requestAnimationFrame(step);
      } else {
        this.scoreEl.textContent = String(target);
        this.scoreEl.classList.add("is-slam");
        sfx.play("scoreSlam");
        setTimeout(() => this.scoreEl.classList.remove("is-slam"), 500);
      }
    };
    this.scoreEl.classList.remove("is-slam");
    this.raf = requestAnimationFrame(step);
  }

  private drawHeatmap(errors: Record<string, number>): void {
    const ctx = this.heatmap.getContext("2d");
    if (!ctx) return;
    const w = this.heatmap.width;
    const h = this.heatmap.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#141a18";
    ctx.fillRect(0, 0, w, h);
    const max = Math.max(1, ...Object.values(errors));
    const cell = w / 12;
    const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    for (let pc = 0; pc < 12; pc++) {
      let count = 0;
      for (const [k, v] of Object.entries(errors)) {
        if (Number(k) % 12 === pc) count += v;
      }
      const intensity = count / max;
      ctx.fillStyle = `rgba(220,70,80,${0.15 + intensity * 0.85})`;
      ctx.fillRect(pc * cell + 1, 6, cell - 2, h - 22);
      ctx.fillStyle = "rgba(255,246,232,0.75)";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(names[pc], pc * cell + cell / 2, h - 4);
    }
  }

  private printReport(): void {
    const r = this.lastReport;
    if (!r) return;
    const win = window.open("", "_blank", "width=720,height=900");
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>${escape(r.title)} — Report</title>
<style>
  body{font-family:Georgia,"Times New Roman",serif;background:#f4efe6;color:#1a1814;padding:2rem;max-width:640px;margin:0 auto}
  h1{letter-spacing:.08em;font-size:1.4rem;border-bottom:2px solid #1a1814;padding-bottom:.4rem}
  .grade{font-size:3.5rem;font-weight:700}
  table{width:100%;border-collapse:collapse;margin:1rem 0}
  td{padding:.4rem .2rem;border-bottom:1px solid #ccc}
  td:last-child{text-align:right;font-weight:600}
  .foot{margin-top:2rem;font-size:.85rem;color:#555}
  .glossary{margin-top:1.25rem;font-size:.9rem}
  .glossary dt{font-weight:700;margin-top:.5rem}
</style></head><body>
  <h1>ULTIMATE PIANO TUTOR — REPORT CARD</h1>
  <p><strong>${escape(r.title)}</strong><br/>${escape(r.subtitle)}</p>
  <p class="grade">${escape(r.letter)}</p>
  <table>
    <tr><td>Accuracy</td><td>${r.accuracyPct.toFixed(1)}%</td></tr>
    <tr><td>Notes / min (NPM)</td><td>${r.npm}</td></tr>
    <tr><td>Hits / Misses</td><td>${r.hits} / ${r.wrongs}</td></tr>
    <tr><td>Perfect hits</td><td>${r.perfect}</td></tr>
    <tr><td>Timing jitter</td><td>${r.jitterMs.toFixed(0)} ms</td></tr>
    <tr><td>Timing bias</td><td>${r.meanDeltaMs >= 0 ? "+" : ""}${r.meanDeltaMs.toFixed(0)} ms</td></tr>
    <tr><td>Dynamics</td><td>${escape(r.dynamicsNote)}</td></tr>
    <tr><td>Time</td><td>${Math.round(r.elapsedSec)}s</td></tr>
  </table>
  <div class="glossary">
    <dt>Jitter</dt><dd>How steady your timing is (spread around your average).</dd>
    <dt>Bias</dt><dd>Whether you tend early (−) or late (+) versus the beat.</dd>
    <dt>NPM</dt><dd>Notes cleared per minute.</dd>
  </div>
  ${r.passLine ? `<p><em>${escape(r.passLine)}</em></p>` : ""}
  <p class="foot">Print this page → Save as PDF · Classic typing-tutor style progress sheet</p>
  <script>window.onload=()=>window.print()</script>
</body></html>`);
    win.document.close();
  }

  hide(): void {
    cancelAnimationFrame(this.raf);
    this.root.hidden = true;
    this.root.classList.remove("is-on", "is-win", "is-lose", "is-report");
    this.root.setAttribute("aria-hidden", "true");
    const cb = this.onClose;
    const action = this.closeAction;
    this.onClose = null;
    cb?.(action);
  }

  get visible(): boolean {
    return !this.root.hidden;
  }
}

function escape(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escape(s).replace(/"/g, "&quot;");
}
